<script setup lang="ts">
    import VatRateCollection from '../../../app/invoicing/collections/VatRateCollection';
    import EndpointFactory from '~~/app/factories/EndpointFactory';
    import ContactSchema from '~~/app/contact/schemas/ContactSchema';
    import { useEvent, type ApplicationEvents } from '@/composables/useEventBus';
    import ContactEndpoint from '~~/app/contact/endpoints/ContactEndpoint';
    import { ModelType } from '~~/app/base/schemas/BaseSchema';
    import Contact from '~~/app/contact/models/Contact';
    import { ValidatedVatNumberResponse } from '~~/app/common/endpoints/ValidateVatNumberEndpoint';
    import { ValidatedSiretResponse } from '@/app/common/endpoints/ValidateSiretEndpoint';
    import InvoicePaymentReminderGroup from '~/app/invoicing/models/InvoicePaymentReminderGroup';
    import InvoicePaymentReminderGroupCollection from '~/app/invoicing/collections/InvoicePaymentReminderGroupCollection';
    import { CONTACT_TYPE, ContactType } from '~/app/contact/enums/ContactType';

    interface Fields {
        vatNumber: string;
        siret: string;
        companyRegistrationNumber: string;
        glnNumber: string;
        lastName: string;
        firstName: string;
        companyName: string;
        contactType: string;
        email: string | null;
        phone: string | null;
        address: Address | null;
        billingAddress: Address | null;
        isProfessional: boolean;
        locale: string;
        vatRate: string;
        honorificPrefix: string;
        groupId: string;
    }

    type ModalPayload = FormModalPayload<Contact, Fields> & {
        openFromInvoiceForm: boolean;
    };

    const translation = useTranslation();
    const form = useForm<Fields>({
        vatNumber: '',
        siret: '',
        companyRegistrationNumber: '',
        glnNumber: '',
        lastName: '',
        firstName: '',
        companyName: '',
        email: '',
        phone: '',
        address: null,
        billingAddress: null,
        contactType: CONTACT_TYPE.CLIENT,
        isProfessional: false,
        locale: translation?.getCurrentLocale() || useGetDefaultLocale(),
        vatRate: '',
        honorificPrefix: '',
        groupId: '',
    });

    const displayBillingAddress = ref(false);
    const displayHonorificPrefix = ref(false);
    const displayAsProfessional = ref<boolean>(true);
    const isOpenFromInvoiceForm = ref<boolean>(false);
    const activeTab = ref<ContactType>(CONTACT_TYPE.CLIENT);

    const { loading: loadingGroups, collection, load } = useCollectionLoader<InvoicePaymentReminderGroup, InvoicePaymentReminderGroupCollection>(ModelType.INVOICE_PAYMENT_REMINDER_GROUPS);
    const { modalName, model } = useModelFormModal<Contact, Fields>(
        Contact,
        async (payload?: ModalPayload) => {
            loading.value = true;
            load();
            if (payload?.openFromInvoiceForm) isOpenFromInvoiceForm.value = payload?.openFromInvoiceForm;
            await fetchVatRates();
            await fillForm(payload);
            loading.value = false;
        },
        () => {
            isOpenFromInvoiceForm.value = false;
            form.reset();
        }
    );
    const loading = ref<boolean>(false);
    const fillForm = async (payload?: FormModalPayload<Contact, Fields>) => {
        form.reset();
        form.set('locale', translation?.getCurrentLocale() || useGetDefaultLocale());

        // Set contactType from payload if provided (prefill the correct tab as context),
        // while keeping the client/supplier tab selector visible.
        if (payload?.fields && payload?.fields.contactType) {
            form.set('contactType', payload?.fields.contactType);
        }

        await fillWithCompleteModel(payload);

        // Set activeTab after model is loaded, using the contactType from the form (which was filled by fillWithCompleteModel)
        const contactType = form.get('contactType');
        if (contactType && contactType !== CONTACT_TYPE.SNAPSHOT) {
            activeTab.value = contactType;
        } else {
            activeTab.value = CONTACT_TYPE.CLIENT;
        }

        if (form.get('billingAddress')) {
            displayBillingAddress.value = true;
        }
        if (form.get('honorificPrefix')) {
            displayHonorificPrefix.value = true;
        }
    };

    // Watch activeTab to update contactType
    watch(activeTab, (newTab) => {
        if (newTab !== CONTACT_TYPE.SNAPSHOT) {
            form.set('contactType', newTab);
        }
    });

    const fillWithCompleteModel = async (payload?: FormModalPayload<Contact, Fields>): Promise<void> => {
        if (!payload || !payload.model) return;

        const endpoint = EndpointFactory.make(ModelType.CONTACTS);
        const response = await endpoint.addInclude('invoices,defaultVatRate,invoicePaymentReminderGroup').retrieve(payload.model.getId());
        if (response.error || !response.data) {
            useToasteoError();
            return;
        }

        const contact = response.data;
        form.fillWithModel(contact);
        model.value = contact;
        form.set('groupId', contact.invoicePaymentReminderGroup?.getId() || getDefaultGroup());
        form.set('vatRate', contact.defaultVatRate?.getId() || '');
    };

    const getDefaultGroup = () => {
        return collection.value?.filter((group: InvoicePaymentReminderGroup) => group.default)[0]?.getId() ?? '';
    };

    watch(collection, (newCollection) => {
        if (newCollection && !form.get('groupId')) {
            form.set('groupId', getDefaultGroup());
        }
    });

    const vatRates = ref<VatRateCollection | null | undefined>();
    const fetchVatRates = async () => {
        const endpoint = EndpointFactory.make(ModelType.VAT_RATES);
        const response = await endpoint.index();
        if (response.error) {
            if (!response.validationErrors) {
                useToasteoError();
            }
            return;
        }
        vatRates.value = response.data;
    };

    const prefillFormWithVATNumber = (response: ValidatedVatNumberResponse) => {
        if (!form.get('companyName') && response.data?.company) {
            form.set('companyName', response.data.company);
        }
        if (!form.get('address') && response.data?.address) {
            form.set('address', response.data.address.display);
        }
    };

    const prefillFormWithSiret = (response: ValidatedSiretResponse) => {
        if (!form.get('companyName') && response.data?.company) {
            form.set('companyName', response.data.company);
        }
        if (!form.get('address') && response.data?.address) {
            form.set('address', response.data.address.display);
        }
    };

    const submit = async () => {
        const data = form.dataExcept(['vatRate', 'groupId']);
        delete data.isProfessional;
        const schema = ContactSchema.make({ attributes: data });
        if (!displayBillingAddress.value) {
            schema.attributes!.billingAddress = null;
        }

        if (form.get('isProfessional') !== true) {
            schema.attributes!.companyName = null;
            schema.attributes!.vatNumber = null;
            schema.attributes!.siret = null;
            schema.attributes!.companyRegistrationNumber = null;
            schema.attributes!.glnNumber = null;
        }

        if (form.get('vatRate')) {
            schema.addToOneRelationship('defaultVatRate', ModelType.VAT_RATES, form.get('vatRate'));
        }
        if (form.get('groupId')) {
            schema.addToOneRelationship('invoicePaymentReminderGroup', ModelType.INVOICE_PAYMENT_REMINDER_GROUPS, form.get('groupId'));
        }
        if (model.value) {
            schema.id = model.value?.getId();
        }

        const endpoint: ContactEndpoint = EndpointFactory.make(ModelType.CONTACTS) as ContactEndpoint;
        const response = await form.loadUntil(endpoint.addInclude('worksites,invoices,defaultVatRate,invoicePaymentReminderGroup').storeOrUpdate(schema));

        if (response.error) {
            if (!response.validationErrors) {
                useToasteoError();
            }
            return;
        }

        close();
    };

    const close = () => {
        form.reset();
        useEvent(`${modalName}:close` as keyof ApplicationEvents);
    };

    const tabsConfig = computed(() => ({
        items: [
            { label: 'contact.contactType.client', value: CONTACT_TYPE.CLIENT },
            { label: 'contact.contactType.supplier', value: CONTACT_TYPE.SUPPLIER },
        ],
    }));

    const selectableVatRates = computed(() => {
        const value = form.fields.vatRate;

        return vatRates.value?.filter((vatRate) => vatRate.isSelectable || vatRate.getId() === value) ?? [];
    });
</script>

<template>
    <ModelFormModal
        :model="Contact"
        scrollable
        modal-style="centered_mobile_full"
        @before-close="form.reset()"
    >
        <ModalLoader v-if="loading || loadingGroups"></ModalLoader>
        <div v-else>
            <div :class="$theme('modal.title.container')">
                <h2 class="font-bricolage font-extrabold text-[22px] leading-[26px] tracking-[-0.03em] text-black lg:text-2xl lg:font-semibold lg:tracking-normal lg:font-inter lg:text-gray-800">
                    {{ model ? $t('contact.contacts.edit_form.title') : $t('contact.contacts.create_form.title') }}
                </h2>
            </div>

            <!-- Tabs Client/Supplier -->

            <form @submit.prevent="submit">
                <!-- md:pr-[10px] compensates the 14px scrollbar so left/right padding stays balanced. -->
                <div :class="$theme('modal.padding') + ' mb-4 md:pr-[10px]'">
                    <AlertBox v-if="model">
                        <div>
                            <p v-if="model.getAttributes().contactType === CONTACT_TYPE.SNAPSHOT">
                                {{ $t('contact.form.edit_warning.client_is_snapshot') }}
                            </p>
                            <p v-else>
                                {{ $t('contact.form.edit_warning.client_is_not_snapshot') }}
                            </p>
                        </div>
                    </AlertBox>

                    <div
                        v-if="form.get('contactType') !== CONTACT_TYPE.SNAPSHOT"
                        class="mb-4"
                    >
                        <UiTabs
                            v-model="activeTab"
                            :content="tabsConfig"
                            :full-width="true"
                        />
                    </div>
                    <ContactContactFormModalExistsAlert
                        v-if="displayAsProfessional"
                        :id="model?.getId()"
                        :vat-number="form.get('vatNumber')"
                        :contact-type="form.get('contactType')"
                        :email="form.get('email')"
                    />

                    <!-- Professional client toggle (kept out of the collapse) -->
                    <div class="mb-2 lg:mb-3">
                        <FormSmoothToggleSwitch
                            :model-value="form.get('isProfessional')"
                            :label="$t('form.labels.isProfessional')"
                            @update:model-value="form.set('isProfessional', $event)"
                        />
                    </div>

                    <!-- Company fields shown above the name when professional -->
                    <TransitionFadeSlide>
                        <div v-if="form.get('isProfessional')">
                            <FormInput
                                :form="form"
                                input-name="companyName"
                            >
                                <template #label-right>
                                    <CommonInfoBadge :text="$t('app.common.badges_info.pro')" />
                                </template>
                            </FormInput>
                            <FormTwoFields>
                                <template #field-1>
                                    <FormVatNumber
                                        :form="form"
                                        input-name="vatNumber"
                                        @valid-vat-number="prefillFormWithVATNumber"
                                    />
                                </template>
                                <template #field-2>
                                    <FormSiret
                                        :form="form"
                                        input-name="siret"
                                        @valid-siret="prefillFormWithSiret"
                                    />
                                </template>
                            </FormTwoFields>
                        </div>
                    </TransitionFadeSlide>

                    <!-- Core fields: Nom, Prénom, E-mail, Téléphone -->
                    <FormInput
                        :form="form"
                        input-name="lastName"
                    />
                    <FormInput
                        :form="form"
                        input-name="firstName"
                    />
                    <!-- Email -->
                    <FormEmailInput
                        :form="form"
                        input-name="email"
                    />
                    <!-- Phone number -->
                    <FormPhoneInput
                        :key="form.get('locale')"
                        :form="form"
                        input-name="phone"
                    />

                    <!-- Optional fields -->
                    <UiCollapseSection
                        flush-x
                        :content="{ title: $t('actions.display_more_fields') }"
                        :default-open="false"
                    >
                        <template #content>
                            <!-- Payment reminder group -->
                            <div v-if="collection && form.get('contactType') !== CONTACT_TYPE.SNAPSHOT">
                                <LazyFormPaymentReminderGroupsSelect
                                    v-can="'invoicing:invoice_payment_reminder'"
                                    :groups="collection"
                                    :form="form"
                                    input-name="groupId"
                                />
                            </div>
                            <!-- Company registration number & GLN kept in the collapse when professional -->
                            <TransitionFadeSlide>
                                <div v-if="form.get('isProfessional')">
                                    <FormTwoFields>
                                        <template #field-1>
                                            <FormInput
                                                :form="form"
                                                input-name="companyRegistrationNumber"
                                            />
                                        </template>
                                        <template #field-2>
                                            <FormInput
                                                :form="form"
                                                input-name="glnNumber"
                                            />
                                        </template>
                                    </FormTwoFields>
                                </div>
                            </TransitionFadeSlide>
                            <div class="mb-2 lg:mb-3">
                                <FormSmoothToggleSwitch
                                    v-model="displayHonorificPrefix"
                                    class="mb-2"
                                    :label="$t('form.labels.honorificPrefix')"
                                    @update:model-value="!$event && form.set('honorificPrefix', '')"
                                />

                                <TransitionFadeSlide>
                                    <FormPrefixInput
                                        v-if="displayHonorificPrefix"
                                        :form="form"
                                        input-name="honorificPrefix"
                                    />
                                </TransitionFadeSlide>
                            </div>

                            <!-- Postal address -->
                            <FormAddressAutocomplete
                                :form="form"
                                input-name="address"
                            />

                            <!-- Different billing address toggle -->
                            <div class="mb-2 lg:mb-3">
                                <FormSmoothToggleSwitch
                                    v-model="displayBillingAddress"
                                    :label="$t('contact.contact_form_modal.display_billing_address_field')"
                                />
                            </div>
                            <TransitionFadeSlide>
                                <template v-if="displayBillingAddress">
                                    <FormAddressAutocomplete
                                        :form="form"
                                        input-name="billingAddress"
                                        :tooltip="$t('contact.billing_address_tooltip')"
                                    />
                                </template>
                            </TransitionFadeSlide>
                            <FormTwoFields>
                                <template #field-1>
                                    <FormLocaleSelect
                                        :form="form"
                                        input-name="locale"
                                    />
                                </template>
                                <template #field-2>
                                    <FormSelect
                                        :form="form"
                                        with-null-option
                                        :label="$t('form.labels.vatRate')"
                                        input-name="vatRate"
                                    >
                                        <option
                                            v-for="vatRate in selectableVatRates"
                                            :key="vatRate.getId()"
                                            :value="vatRate.getId()"
                                        >
                                            {{ vatRate.title }}
                                        </option>
                                    </FormSelect>
                                </template>
                            </FormTwoFields>
                        </template>
                    </UiCollapseSection>
                </div>

                <div :class="$theme('modal.footer.container')">
                    <div class="flex flex-col gap-3">
                        <UiAction
                            :content="{
                                label: 'actions.save',
                                type: 'main',
                                icon: 'fa-regular fa-check',
                            }"
                            modifier="full"
                            :is-loading="form.isLoading"
                            @click="submit()"
                        />
                        <UiAction
                            :content="{
                                label: 'actions.cancel',
                                type: 'light',
                            }"
                            modifier="full"
                            @click="close()"
                        />
                    </div>
                </div>
            </form>
        </div>
    </ModelFormModal>
</template>
